/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect, useState, useMemo } from 'react';
import get from 'lodash/get';
import {
    Button,
    Counter,
    FlexCol,
    FlexRow,
    Icon,
    VisualOnlyLabel,
    StyledModal,
} from 'sarsaparilla';

import TempForm from './multi-division/TempForm';
import SingleDatePickerCalendar from '../containers/single-date-picker-calendar';
import SmartSelector from './registration/SmartSelector';
import { getSelectableDailyDivisions } from '../utils/utilsNew';
import { ANGELS_LANDING_FACILITIES } from '../constants';
// import PropTypes from 'prop-types';

function DailyLotteryPreference({
    permitId,
    selectedDates,
    divisions,
    maxGroupSize,
    selectedStartDate,
    onChange,
    maxPreferences,
    validationErrors,
}) {
    const [numPreferences, setNumPreferences] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        setNumPreferences(selectedDates?.length || 1);
    }, [selectedDates]);

    const handleTempFormSubmit = () => {
        const selections = Array(numPreferences).fill({
            preferred_date: selectedStartDate,
            preferred_range_start: selectedStartDate,
            preferred_range_end: selectedStartDate,
        });
        onChange(selections);
    };

    const handleChange = (index, item) => {
        const selections = [...selectedDates];
        selections[index] = { ...selections[index], ...item };
        onChange(selections);
    };

    const handleRemoveSelection = (index) => {
        const selections = [...selectedDates];
        selections.splice(index, 1);
        onChange(selections);
    };

    const handleAddSelection = () => {
        const selections = [...selectedDates];
        selections.push({
            preferred_date: selectedStartDate,
            preferred_range_start: selectedStartDate,
            preferred_range_end: selectedStartDate,
        });
        onChange(selections);
    };

    const renderTempForm = () => {
        return (
            <TempForm
                value={numPreferences}
                onChange={(val) => setNumPreferences(val)}
                minValue={1}
                maxValue={maxPreferences}
                onSubmit={handleTempFormSubmit}
            />
        );
    };

    const divisionOptions = useMemo(
        () => getSelectableDailyDivisions(divisions),
        [divisions]
    );

    const renderPreferenceInstructionText = () => {
        if (ANGELS_LANDING_FACILITIES.includes(permitId)) {
            return (
                <p id="rec-daily-text">
                    Choose your preferred dates and times below. You can apply up to{' '}
                    <b>{maxPreferences} choices</b>. The more dates you add, the better
                    your chances! The start time range indicates the time you should be at
                    the Grotto trailhead. If you are awarded a permit, your credit card
                    will automatically be charged&nbsp;
                    <Button
                        appearance="link"
                        screenReaderTextBefore="Open modal for more information on"
                        onClick={() => setIsModalOpen(true)}
                        type="button"
                        className="per-inline-button"
                    >
                        Applicable Fees
                    </Button>
                    .
                </p>
            );
        }
        return (
            <p className="rec-daily-text">
                Enter your lottery preferences below. Max group size is {maxGroupSize}{' '}
                people. If you are awarded a permit your credit card will be automatically
                charged&nbsp;
                <Button
                    appearance="link"
                    screenReaderTextBefore="Open modal for more information on"
                    onClick={() => setIsModalOpen(true)}
                    type="button"
                    className="per-inline-button"
                >
                    Applicable Fees
                </Button>
                .
            </p>
        );
    };

    const renderPreferences = () => {
        return (
            <div className="per-preferred-dates-list">
                <div className="per-form-group">
                    {selectedDates?.map((selection, index) => {
                        return (
                            <div
                                className="per-form-group-item"
                                key={`${index}-daily-lottery-selection`}
                            >
                                <div className="per-form-group-item-header">
                                    <div className="per-form-group-item-title">
                                        Choice #{index + 1}
                                    </div>
                                    <Button
                                        appearance="subtle-danger"
                                        id={`removeIcon-${index}`}
                                        iconBeforeElement={<Icon iconName="delete" />}
                                        onClick={() => handleRemoveSelection(index)}
                                        screenReaderTextBefore={`Remove choice ${
                                            index + 1
                                        } from the lottery preference list`}
                                    />
                                </div>
                                <FlexRow>
                                    <FlexCol xs={12} sm={6} md="auto">
                                        <div className="rec-form-item-wrap">
                                            <VisualOnlyLabel
                                                htmlFor="entry-date-picker-daily"
                                                isRequired
                                            >
                                                Entry Date
                                            </VisualOnlyLabel>
                                            <SingleDatePickerCalendar
                                                showDefaultInputIcon
                                                id="entry-date-picker-daily"
                                                divisionId={selection.division_id}
                                                selectedDate={selectedStartDate}
                                                renderLabels={false}
                                                onChange={() => {}}
                                                disabled
                                                readOnly
                                                blockNonLotteryDates
                                            />
                                        </div>
                                    </FlexCol>
                                    <FlexCol xs={12} sm={6} md="auto">
                                        <div className="rec-form-item-wrap">
                                            <SmartSelector
                                                name={`dropdown-division`}
                                                id={`dropdown-division-${index}`}
                                                label="Permit Type"
                                                isRequired
                                                value={selection.division_id}
                                                handleChange={(e) =>
                                                    handleChange(index, {
                                                        division_id: e.target.value,
                                                    })
                                                }
                                                options={divisionOptions}
                                            />
                                        </div>
                                    </FlexCol>
                                    <FlexCol xs={12} sm={6} md="auto">
                                        <div className="rec-form-item-wrap">
                                            <VisualOnlyLabel isRequired>
                                                Group Size
                                            </VisualOnlyLabel>
                                            <Counter
                                                validationClass={get(
                                                    validationErrors,
                                                    'groupMaxRequired',
                                                    ''
                                                )}
                                                id="groupSize"
                                                count={selection.group_max_size}
                                                max={maxGroupSize}
                                                onCountChange={(ct) =>
                                                    handleChange(index, {
                                                        group_min_size: ct,
                                                        group_max_size: ct,
                                                    })
                                                }
                                            />
                                        </div>
                                    </FlexCol>
                                </FlexRow>
                            </div>
                        );
                    })}
                </div>
                <div className="rec-form-item-wrap">
                    <Button
                        appearance="link"
                        iconBeforeElement={<Icon iconName="add-circle" />}
                        onClick={handleAddSelection}
                        isDisabled={selectedDates?.length >= maxPreferences}
                        className="addAnotherChoiceLink"
                    >
                        Add another choice
                    </Button>
                </div>
            </div>
        );
    };

    const renderApplicableFeesModal = () => {
        return (
            <StyledModal
                isOpen={isModalOpen}
                onRequestClose={() => setIsModalOpen(false)}
                role="complementary"
                size="md"
                contentLabel="Lottery Confirm Modal"
                heading={
                    <div className="rec-modal-title">
                        <span className="per-modal-icon">
                            <Icon iconName="info" />
                        </span>
                        Applicable Fees
                    </div>
                }
            >
                <div className="rec-modal-content-wrap">
                    <p>
                        If your application is selected through the lottery, you&apos;ll
                        automatically be awarded a permit and applicable use fees will be
                        charged to the credit or debit card used for your application. If
                        the charge to the credit or debit card is declined for any reason,
                        the reservation will be forfeited.
                    </p>
                </div>
            </StyledModal>
        );
    };

    return (
        <div className="per-preferred-dates-form per-section-group">
            <div className="rec-order-detail-subsection-title">Lottery Preferences</div>
            {renderPreferenceInstructionText()}
            {selectedDates?.length === 0 && renderTempForm()}
            {selectedDates?.length > 0 && renderPreferences()}
            {renderApplicableFeesModal()}
        </div>
    );
}

export default DailyLotteryPreference;
